import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, useWindowDimensions, BackHandler, Keyboard, Image } from 'react-native';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';
import { ThemeConsumer } from '@rneui/themed';
import { Theme } from '@rneui/themed';
import CommonService from '../../../shared/services/commonService';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import Popover from 'react-native-popover-view';
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox';
import { pageDirty } from '../../../redux/slices/commonSlice';
import { appColors } from '../../../themes/appTheme';
import { onCompleteSale, setTotalAmount } from '../../../redux/slices/newSellscreenPaymentSlice';
import { setCheckoutGiftCart } from '../../../redux/slices/sellScreenSlice';
import { config } from '../../../shared/environment';
import LinearGradient from 'react-native-linear-gradient';
import { formatMinor } from '../discounts/discountLabels';

const SellscreenSummary = () => {
	const navigation = useAppNavigation()
	const dispatch = useAppDispatch()
	const { width, height } = useWindowDimensions()
	const { customerDetails, cartProducts, summary, tip, additionalCharge, customerNotes, cartQuantity, sellScreenType, pricedCart } = useAppSelector((state) => state?.NewSellscreenSlice)
	const { transactionData } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const { isPageDirty } = useAppSelector((state) => state?.common)
	const selectedCustomer = useAppSelector((state) => state?.sellScreen?.selectedCustomer)
	const data = useAppSelector(state => state?.sellScreen?.checkoutGiftCart)
	const storeName = useAppSelector(state => state.common?.currentUser?.value?.store?.name)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const { subTotal, couponDiscount, additionalDiscount, otherDiscount, tax, grandTotal, discountedPrice, roundOff, payableTotal } = summary?.[locationId] || {}
	const totalSaved = subTotal - discountedPrice

	const formatPhoneNumber = (phone?: string | number, countryCode?: string | number) => {
		if (!phone) return 'N/A';
		const trimmedPhone = phone.toString().trim();
		if (trimmedPhone.startsWith('+')) return trimmedPhone;
		if (countryCode && Number(countryCode) > 0) {
			return `+${countryCode} ${trimmedPhone}`;
		}
		return trimmedPhone;
	};

	const isGiftToFriend = Boolean(data?.isGiftToFriend);

	const recipientName = isGiftToFriend
		? (data?.recipientName || 'N/A')
		: (customerDetails?.name || selectedCustomer?.name || (customerDetails?.firstName ? `${customerDetails.firstName} ${customerDetails.lastName || ''}`.trim() : '') || 'N/A');

	const recipientEmail = isGiftToFriend
		? (data?.recipientEmail || 'N/A')
		: (customerDetails?.email || selectedCustomer?.email || 'N/A');

	const recipientPhone = isGiftToFriend
		? formatPhoneNumber(data?.recipientPhone, data?.countryCode)
		: formatPhoneNumber(customerDetails?.phone || selectedCustomer?.phone, customerDetails?.countryCode);

	const giftCardImageUrl = data?.giftCartItem?.imageUrl
		? (data?.giftCartItem?.imageUrl?.startsWith('http')
			? data?.giftCartItem?.imageUrl
			: config?.AWSImageUrl + data?.giftCartItem?.imageUrl)
		: null;

	const giftCardName = data?.giftCartItem?.giftCardName || 'Gift Card';
	const amountToPay = data?.giftCardValue?.value ? data?.giftCardValue?.value?.toString() : '0';

	const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
	const leaveAlertDialogProps = {
		content: "This transaction is not complete. Leaving now will affect the payment. Continue?",
		button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
		icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
	}

	//UseEffect for handle Hardware BackPress
	useEffect(() => {
		const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
			if (transactionData?.length > 0) {
				setLeavePopoverVisible(true)
				return true
			}
			navigation.goBack()
			dispatch(setCheckoutGiftCart(null))
			return true
		});
		return () => backHandler?.remove()
	}, [transactionData])

	useEffect(() => {
		if (transactionData?.length > 0) {
			!isPageDirty && dispatch(pageDirty(true))
		} else {
			isPageDirty && dispatch(pageDirty(false))
		}
	}, [transactionData])

	//method for popover confirmation 
	const confirmation = (item: { id: number }) => {
		setLeavePopoverVisible(false)
		if (item?.id == 1) {
			navigation?.goBack()
			dispatch(onCompleteSale())
			dispatch(pageDirty(false))
			dispatch(setCheckoutGiftCart(null))
		}
	}

	const handleHeaderBackPress = async () => {
		Keyboard?.dismiss()
		if (transactionData?.length > 0) {
			setLeavePopoverVisible(true)
		}
		else {
			dispatch(setCheckoutGiftCart(null))
			navigation?.goBack()
		}
	}

	// sign overrides the default (- for discounts, + otherwise); pass "" for a plain amount
	const SummaryRow = ({ label, value, isDiscount = false, isBold = false, sign }: any) => (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.summaryRow}>
						<ZenText content={label} style={[styles.summaryLabel, isBold && styles.boldText]} />
						<ZenText content={(sign ?? (isDiscount ? '-' : '+')) + CommonService?.currencyStandard(currencySymbol, value?.toString() || "0")} style={[
							styles.summaryValue,
							isDiscount && styles.discountValue,
							isBold && styles.boldText,
							isBold && { color: theme.lightDark, fontSize: 16 }
						]} />
					</View>
				)
			}}
		</ThemeConsumer>
	);

	const ProductRow = ({ label, value, isDiscount = false, }: any) => (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.detailColumn}>
						<ZenText content={label} style={styles.detailLabel} />
						{isDiscount ? <View style={styles.discountBadge}>
							<ZenText content={value} style={styles.discountText} />
						</View> : <ZenText content={value} style={styles.detailValue} />}
					</View>
				)
			}}
		</ThemeConsumer>
	);

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.mainContainer}>

						<View style={styles.header}>
							<ZenIcon name={"arrow-left"} type={"material-community"} size={width > 600 ? 28 : 22} color={theme?.dark} onPress={handleHeaderBackPress} />
							<ZenText content={data?.giftCardValue ? "Gift card checkout" : "Order Summary"} style={styles.headerTitle} />
						</View>

						<ScrollView showsVerticalScrollIndicator={false}>

							{data?.giftCardValue ? (
								<View style={styles.giftCardSummaryWrapper}>
									{/* Top Highlight Banner */}
									<View style={styles.giftCardBanner}>
										<ZenIcon name="gift" type="feather" size={20} color={theme?.primary || '#005374'} />
										<ZenText content="Digital Gift Card Order Summary" style={styles.giftCardBannerText} />
									</View>

									{/* Left Column: Gift Card Card */}
									<View style={[styles.section, { flex: 1, minWidth: 280, paddingVertical: 0 }]}>
										<ZenText content="GIFT CARD PREVIEW" style={styles.sectionHeader} />
										<View style={[styles.card, { padding: 18 }]}>
											<View style={styles.giftCardVisualCard}>
												{giftCardImageUrl ? (
													<Image
														source={{ uri: config?.AWSImageUrl + giftCardImageUrl }}
														style={{ width: '100%', height: '100%', borderRadius: 14, resizeMode: 'cover' }}
													/>
												) : (
													<LinearGradient
														colors={[theme.primary || '#005374', '#007991', '#00abc7']}
														start={{ x: 0, y: 0 }}
														end={{ x: 1, y: 1 }}
														style={styles.cardGradientContent}
													>
														<View style={styles.cardHeaderRow}>
															<View style={styles.cardBadge}>
																<ZenIcon name="card-giftcard" type="material" size={16} color="#FFFFFF" />
																<ZenText content="GIFT CARD" style={styles.cardBadgeTxt} />
															</View>
															<ZenIcon name="wifi" type="feather" size={18} color="rgba(255,255,255,0.7)" />
														</View>

														<View style={styles.cardBodyCenter}>
															<ZenText content={giftCardName} style={styles.cardTitleTxt} numberOfLines={2} />
														</View>

														<View style={styles.cardFooterRow}>
															<ZenText content={storeName} style={styles.cardBrandTxt} />
															<View style={styles.cardAmountBadge}>
																<ZenText content={CommonService?.currencyStandard(currencySymbol, amountToPay)} style={styles.cardAmountBadgeTxt} />
															</View>
														</View>
													</LinearGradient>
												)}
											</View>

											<View style={styles.giftCardMetaRow}>
												<View style={[styles.tagBadge, { backgroundColor: '#e0f2fe' }]}>
													<ZenIcon name="check-circle" type="feather" size={13} color="#0284c7" />
													<ZenText content="Instant Digital Delivery" style={[styles.tagBadgeText, { color: '#0369a1' }]} />
												</View>
												<View style={[styles.tagBadge, { backgroundColor: '#f3e8ff' }]}>
													<ZenIcon name="clock" type="feather" size={13} color="#9333ea" />
													<ZenText content={data?.giftCartItem?.giftCardDays ? `${data.giftCartItem.giftCardDays} Days Expiry` : 'Never Expires'} style={[styles.tagBadgeText, { color: '#7e22ce' }]} />
												</View>
											</View>
										</View>
									</View>

									{/* Right Column: Recipient & Order Details */}
									<View style={[styles.section, { flex: 1, minWidth: 280, paddingVertical: 0 }]}>
										<ZenText content="RECIPIENT & SUMMARY" style={styles.sectionHeader} />
										<View style={[styles.card, { padding: 18, gap: 12 }]}>
											{/* Name Tile */}
											<View style={[styles.infoTile, { backgroundColor: '#f0f9ff', borderColor: '#bae6fd' }]}>
												<View style={[styles.infoTileIconBox, { backgroundColor: '#0284c7' }]}>
													<ZenIcon name="user" type="feather" size={16} color="#FFFFFF" />
												</View>
												<View style={styles.infoTileTextGroup}>
													<ZenText content="RECIPIENT NAME" style={[styles.infoTileLabel, { color: '#0369a1' }]} />
													<ZenText content={recipientName} style={styles.infoTileValue} numberOfLines={2} />
												</View>
											</View>

											{/* Email Tile */}
											<View style={[styles.infoTile, { backgroundColor: '#faf5ff', borderColor: '#e9d5ff' }]}>
												<View style={[styles.infoTileIconBox, { backgroundColor: '#9333ea' }]}>
													<ZenIcon name="mail" type="feather" size={16} color="#FFFFFF" />
												</View>
												<View style={styles.infoTileTextGroup}>
													<ZenText content="RECIPIENT EMAIL" style={[styles.infoTileLabel, { color: '#7e22ce' }]} />
													<ZenText content={recipientEmail} style={styles.infoTileValue} numberOfLines={2} />
												</View>
											</View>

											{/* Phone Tile */}
											<View style={[styles.infoTile, { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }]}>
												<View style={[styles.infoTileIconBox, { backgroundColor: '#10b981' }]}>
													<ZenIcon name="phone" type="feather" size={16} color="#FFFFFF" />
												</View>
												<View style={styles.infoTileTextGroup}>
													<ZenText content="CONTACT NUMBER" style={[styles.infoTileLabel, { color: '#047857' }]} />
													<ZenText content={recipientPhone} style={styles.infoTileValue} numberOfLines={2} />
												</View>
											</View>

											{/* Gift Message Tile (If exists) */}
											{data?.message ? (
												<View style={[styles.infoTile, { backgroundColor: '#fffbeb', borderColor: '#fef08a' }]}>
													<View style={[styles.infoTileIconBox, { backgroundColor: '#f59e0b' }]}>
														<ZenIcon name="message-square" type="feather" size={16} color="#FFFFFF" />
													</View>
													<View style={styles.infoTileTextGroup}>
														<ZenText content="GIFT MESSAGE" style={[styles.infoTileLabel, { color: '#b45309' }]} />
														<ZenText content={`"${data.message}"`} style={[styles.infoTileValue, { fontStyle: 'italic' }]} numberOfLines={3} />
													</View>
												</View>
											) : null}

											{/* Amount to Pay Container */}
											<View style={styles.grandTotalContainer}>
												<View>
													<ZenText content="Total Amount to Pay" style={styles.grandTotalLabel} />
													<ZenText content={CommonService?.currencyStandard(currencySymbol, amountToPay)} style={styles.grandTotalValue} />
												</View>
												<View style={styles.paidBadgeTag}>
													<ZenIcon name="shield-check" type="material-community" size={16} color={theme?.primary || '#005374'} />
													<ZenText content="Secure" style={styles.paidBadgeTagTxt} />
												</View>
											</View>
										</View>
									</View>
								</View>
							) : (
								<>
									{/* Total Savings High-light */}
									{totalSaved ? <View style={styles.savingsBanner}>
										<ZenText content={`🎉 You saved a total of `} style={styles.savingsBannerText} />
										<ZenText content={CommonService?.currencyStandard(currencySymbol, totalSaved?.toString() || "0")} style={[styles.savingsBannerText, { fontFamily: theme?.bold }]} />
										<ZenText content={` on this order!`} style={styles.savingsBannerText} />
									</View> : null}

									{/* Product List Section (Previous Design) */}
									< View style={styles.section} >
										<ZenText content="Bill Details" style={styles.sectionHeader} />

										{/* Summary Card */}
										< View style={styles.card} >
											<SummaryRow label="Subtotal" value={subTotal} />
											<SummaryRow label="Coupon Discount" value={couponDiscount} isDiscount />
											<SummaryRow label="Additional Discount" value={additionalDiscount} isDiscount />
											<SummaryRow label="Item Discounts" value={otherDiscount} isDiscount />

											<View style={styles.divider} />

											<SummaryRow label="Tax Amount" value={tax} />
											<SummaryRow label="Additional Charges" value={additionalCharge?.[locationId]?.amount} />
											<SummaryRow label="Tip" value={tip?.[locationId]} />
											{roundOff ? <>
												<View style={styles.divider} />
												<SummaryRow label="Total" value={grandTotal} sign="" isBold />
												<SummaryRow label="Round Off" value={Math.abs(roundOff).toFixed(2)} sign={roundOff > 0 ? "+" : "-"} />
											</> : null}

											{/* Grand Total Section */}
											< View style={styles.grandTotalContainer} >
												<View>
													<View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
														<ZenText content="Grand Total" style={styles.grandTotalLabel} />
														<ZenText content={`(Item: ${cartProducts?.[locationId]?.length}, Qty: ${cartQuantity?.[locationId]})`} style={styles.itemCount} />
													</View>
													<ZenText content={CommonService?.currencyStandard(currencySymbol, (payableTotal ?? grandTotal)?.toString() || "0")} style={styles.grandTotalValue} />
												</View>
												{/* {totalSaved ? <View style={styles.savingsTag}>
													<ZenIcon type='material-community' name="star-face" size={16} color={theme?.light} />
													<ZenText content={"Save " + CommonService?.currencyStandard(currencySymbol, totalSaved?.toString() || "0")} style={styles.savingsTagText} />
												</View> : <></>} */}
											</View>
										</View>
									</View >

									<View style={styles.section}>
										<View style={styles.headerRow}>
											<ZenText content="ITEM DETAILS" style={styles.sectionHeader} />
											<ZenText content="TOTAL" style={[styles.sectionHeader, { paddingLeft: 0, paddingRight: 10 }]} />
										</View>

										<View style={{ gap: 12 }}>
											{cartProducts?.[locationId]?.map((item) => (
												<View key={item.variantId} style={styles.card}>
													<View style={styles.topRow}>
														<ZenText content={item.name} style={styles.productName} />
														<ZenText content={CommonService?.currencyStandard(currencySymbol, item?.finalPrice?.toString() || "0")} style={styles.productTotal} />
													</View>

													<View style={styles.detailsGrid}>
														<ProductRow label="Qty" value={`x${item.quantity}`} />
														<ProductRow label="Unit Price" value={CommonService?.currencyStandard(currencySymbol, item?.price?.toString() || "0")} />
														<ProductRow label="Tax" value={CommonService?.currencyStandard(currencySymbol, item?.tax?.toString() || "0")} />
														<ProductRow label="Discount" value={"-" + CommonService?.currencyStandard(currencySymbol, (sellScreenType === "SELL" ? item?.discount : (item?.autoDiscount || item?.discount))?.toString() || "0")} isDiscount />
													</View>
												</View>
											))}
											{sellScreenType === "SELL" ? pricedCart?.[locationId]?.freeLines?.map((line) => (
												<View key={line.key} style={styles.card}>
													<View style={styles.topRow}>
														<ZenText content={line.name} style={styles.productName} />
														<ZenText content="FREE" style={styles.productTotal} />
													</View>
													<View style={styles.detailsGrid}>
														<ProductRow label="Qty" value={`x${line.quantity}`} />
														<ProductRow label="Unit Price" value={formatMinor(currencySymbol, line.originalUnitPrice)} />
														<ProductRow label="Offer" value={line.title} />
													</View>
												</View>
											)) : null}
										</View>
									</View>

									<View style={{ backgroundColor: theme?.lightGrey, height: 0.8, marginVertical: 10 }} />

									{/* Order Notes Section */}
									<View style={styles.notesContainer}>
										<View style={styles.notesHeader}>
											<ZenIcon type='feather' name="edit-3" size={16} color={theme.lightDark} />
											<ZenText content="Customer Notes" style={styles.notesLabel} />
										</View>
										<ZenText content={customerNotes?.[locationId] || "No notes added"} style={styles.notesText} />
									</View>
								</>
							)}

							{/* For leave confirmation */}
							<Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }} onRequestClose={() => { setLeavePopoverVisible(false) }}>
								<CommonDialogBox type="OTHER" dialog={leaveAlertDialogProps} confirmation={confirmation} />
							</Popover>

						</ScrollView >
					</View>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	mainContainer: {
		flex: 1,
		paddingRight: 5
	},

	header: { flexDirection: 'row', alignItems: 'center', marginBottom: 16, gap: 10 },
	headerTitle: { fontSize: theme?.headerFontSize, fontFamily: theme?.bold, color: theme?.lightDark },

	headerRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	topRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 12,
	},
	productName: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
		color: theme.lightDark,
		flex: 1,
	},
	productTotal: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.bold,
		color: theme.primary,
	},
	detailsGrid: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		borderTopWidth: 1,
		borderTopColor: theme.iceWhite,
		paddingTop: 12,
	},
	detailColumn: {
		// flex: 1,
	},
	alignEnd: {
		alignItems: 'flex-end',
	},
	detailLabel: {
		fontSize: theme?.miniLabel,
		color: theme.disabledGray500,
		marginBottom: 4,
		textTransform: 'uppercase',
		fontFamily: theme?.medium,
	},
	detailValue: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.medium,
		color: theme.lightDark,
	},
	discountBadge: {
		backgroundColor: theme.lightTealGreen,
		paddingHorizontal: 7,
		paddingVertical: 2,
		borderRadius: 4,
	},
	discountText: {
		fontSize: theme?.badgeFontSize,
		fontFamily: theme?.bold,
		color: theme?.tealGreen,
	},


	section: {
		paddingVertical: 16,
	},
	sectionHeader: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.bold,
		color: theme.draggerIcon,
		marginBottom: 12,
		letterSpacing: 1,
		textTransform: 'uppercase',
		paddingLeft: 10
	},
	card: {
		backgroundColor: theme.light,
		borderRadius: 15,
		padding: 16,
		borderWidth: 1.3,
		borderColor: theme?.coolGray,
		elevation: 1,
		shadowColor: theme.thickDark,
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.08,
		shadowRadius: 2,
	},
	summaryRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 10,
	},
	summaryLabel: {
		fontSize: 14,
		color: theme.disabledGray500,
		fontFamily: theme?.medium
	},
	summaryValue: {
		fontSize: 14,
		color: theme.lightDark,
		fontFamily: theme?.bold
	},
	discountValue: {
		color: theme?.tealGreen,
	},
	divider: {
		height: 1,
		backgroundColor: theme.softSlate,
		marginVertical: 15,
	},
	grandTotalContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: 10,
		backgroundColor: theme.iceWhite,
		padding: 16,
		borderRadius: 16,
		borderWidth: 1,
		borderColor: theme.softSlate
	},
	grandTotalLabel: {
		fontSize: theme?.miniFontSize,
		color: theme.disabledGray500,
		fontFamily: theme?.medium,
		textTransform: 'uppercase',
	},
	grandTotalValue: {
		fontSize: theme?.bigFontSize,
		fontFamily: theme?.bold,
		color: theme.lightDark,
	},
	savingsTag: {
		backgroundColor: theme.primary,
		paddingHorizontal: 10,
		paddingVertical: 6,
		borderRadius: 10,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
	},
	savingsTagText: {
		color: theme.light,
		fontSize: theme?.miniLabel,
		fontFamily: theme?.medium
	},
	notesContainer: {
		marginTop: 10,
		marginBottom: 30,
		padding: 16,
		backgroundColor: theme?.light,
		borderRadius: 16,
		borderWidth: 1.3,
		borderColor: theme?.coolGray,
		elevation: 1,
		shadowColor: theme.thickDark,
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.08,
		shadowRadius: 2,
		borderLeftWidth: 4,
		borderLeftColor: theme?.blue600,
		gap: 5
	},
	notesHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
	},
	notesLabel: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.bold,
		color: theme.lightDark,
	},
	notesText: {
		fontSize: theme?.smallHeaderFontSize,
		color: theme?.grey,
	},
	savingsBanner: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: 10,
		backgroundColor: theme?.lightTealGreen,
		paddingVertical: 16,
		paddingHorizontal: 13,
		borderRadius: 16,
		borderWidth: 1,
		borderColor: theme?.green50,
	},
	savingsBannerText: {
		color: theme?.darkGreen,
		fontSize: theme?.errorFontSize,
		textAlign: 'center',
	},
	boldSavings: {
		fontFamily: theme?.bold
	},
	boldText: {
		fontFamily: theme?.bold
	},
	itemCount: {
		fontSize: theme?.miniFontSize,
		fontFamily: theme?.medium,
		color: theme?.draggerIcon
	},
	giftCardSummaryWrapper: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 16,
		paddingVertical: 12,
	},
	giftCardBanner: {
		width: '100%',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		backgroundColor: theme?.tealPrimary + "20",
		borderWidth: 1,
		borderColor: theme?.primary + "35",
		paddingVertical: 12,
		paddingHorizontal: 16,
		borderRadius: 14,
		marginBottom: 4,
	},
	giftCardBannerText: {
		fontSize: theme?.smallHeaderFontSize,
		fontFamily: theme?.bold,
		color: theme?.primary || '#005374',
	},
	giftCardVisualCard: {
		width: '100%',
		height: 185,
		borderRadius: 14,
		overflow: 'hidden',
		marginBottom: 14,
		elevation: 3,
		shadowColor: theme?.primary || '#005374',
		shadowOffset: { width: 0, height: 3 },
		shadowOpacity: 0.15,
		shadowRadius: 6,
	},
	cardGradientContent: {
		flex: 1,
		padding: 16,
		justifyContent: 'space-between',
	},
	cardHeaderRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	cardBadge: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		backgroundColor: 'rgba(255, 255, 255, 0.25)',
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 20,
	},
	cardBadgeTxt: {
		fontSize: 10,
		fontFamily: theme?.bold,
		color: '#FFFFFF',
		letterSpacing: 1,
	},
	cardBodyCenter: {
		marginVertical: 8,
	},
	cardTitleTxt: {
		fontSize: 20,
		fontFamily: theme?.bold,
		color: '#FFFFFF',
		textShadowColor: 'rgba(0, 0, 0, 0.3)',
		textShadowOffset: { width: 0, height: 1 },
		textShadowRadius: 3,
	},
	cardFooterRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	cardBrandTxt: {
		fontSize: 11,
		fontFamily: theme?.bold,
		color: 'rgba(255, 255, 255, 0.85)',
		letterSpacing: 1.5,
	},
	cardAmountBadge: {
		backgroundColor: '#FFFFFF',
		paddingHorizontal: 12,
		paddingVertical: 4,
		borderRadius: 12,
		shadowColor: '#000000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 4,
	},
	cardAmountBadgeTxt: {
		fontSize: 15,
		fontFamily: theme?.bold,
		color: theme?.primary || '#005374',
	},
	giftCardMetaRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
	},
	tagBadge: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		paddingHorizontal: 10,
		paddingVertical: 6,
		borderRadius: 8,
	},
	tagBadgeText: {
		fontSize: theme?.miniLabel || 12,
		fontFamily: theme?.medium,
	},
	infoTile: {
		flexDirection: 'row',
		alignItems: 'center',
		padding: 12,
		borderRadius: 12,
		borderWidth: 1,
	},
	infoTileIconBox: {
		width: 36,
		height: 36,
		borderRadius: 18,
		justifyContent: 'center',
		alignItems: 'center',
	},
	infoTileTextGroup: {
		flex: 1,
		marginLeft: 12,
	},
	infoTileLabel: {
		fontSize: 10,
		fontFamily: theme?.bold,
		letterSpacing: 0.5,
		marginBottom: 2,
	},
	infoTileValue: {
		fontSize: 14,
		fontFamily: theme?.bold,
		color: theme?.lightDark || '#0f172a',
	},
	paidBadgeTag: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
		backgroundColor: theme?.tealPrimary + "30",
		paddingHorizontal: 10,
		paddingVertical: 5,
		borderRadius: 8,
	},
	paidBadgeTagTxt: {
		fontSize: 12,
		fontFamily: theme?.bold,
		color: theme?.primary || '#005374',
	},
});

export default SellscreenSummary;